import Loading from '../../../components/Loading';
import {CoworkBarePanel} from '../../../components/admin/CoworkFormPanel';
import './pagoSesion.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import DocumentoPagoSelector, { DocumentoPago } from '../../../components/pagos/DocumentoPagoSelector';
import { consultarDocumentoSesion } from '../../../services/facturacionService';
import { createPago } from '../../../services/pagosService';
import { TipoPago, fetchTiposPagoPorPos } from '../../../services/tiposPagoService';
import {
    fetchSesionesAbiertasPorUsuario,
    obtenerSesionActivaPorId,
    SesionActivaResponse,
    SesionPOSLookup,
} from '../../../services/pos/sesionPOSService';
import { Sesion } from '../../../services/sesionesService';
import { OperadorGrantRow, OperadorPinDialog, useOperadorGrant } from '../../../components/pos/OperadorPinDialog';
import { Banknote, CreditCard, X, Smartphone, Wallet, CheckCircle, AlertCircle, ExternalLink, Lock, SplitSquareHorizontal } from 'lucide-react';

interface PagoSesionModalProps {
    abierto: boolean;
    onCerrar: () => void;
    sesion: Sesion | null;
    clienteId: string | null;
    onPagoExitoso: () => void;
}

/**
 * Cobro de una sesión de tratamiento FUERA del contexto POS (kardex, "mis
 * citas"). La caja es explícita: se listan las cajas abiertas operables por
 * el usuario (``fetchSesionesAbiertasPorUsuario``); con una sola se toma de
 * forma determinista, con varias se exige elegir (nunca se usa el endpoint
 * singular, que responde 409 con varias cajas). La caja elegida se revalida
 * por id antes de habilitar el cobro y el grant del operador (PIN) queda
 * ligado a esa caja: cambiar de caja lo descarta.
 *
 * Toda continuación asíncrona (descubrimiento, revalidación, cobro y
 * callbacks) exige que el modal siga montado y abierto, que la sesión de
 * tratamiento y la caja sigan siendo las capturadas y que ningún cierre haya
 * incrementado la generación: un resultado tardío nunca muestra éxito, ni
 * notifica, ni escribe estado bajo otra identidad. El éxito solo se muestra
 * DESPUÉS de que ``createPago`` resuelva, sin esperas artificiales.
 */
const PagoSesionModal: React.FC<PagoSesionModalProps> = ({
    abierto,
    onCerrar,
    sesion,
    clienteId,
    onPagoExitoso
}) => {
    const [tiposPago, setTiposPago] = useState<TipoPago[]>([]);
    const [tipoPagoId, setTipoPagoId] = useState<number | ''>('');
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState<TipoPago | null>(null);
    const [efectivoRecibido, setEfectivoRecibido] = useState<number>(0);
    const [cambio, setCambio] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cajasAbiertas, setCajasAbiertas] = useState<SesionPOSLookup[]>([]);
    const [cajaSeleccionadaId, setCajaSeleccionadaId] = useState<number | null>(null);
    const [sesionActiva, setSesionActiva] = useState<SesionActivaResponse | null>(null);
    const [verificandoSesion, setVerificandoSesion] = useState(false);
    const [showNoSessionPopup, setShowNoSessionPopup] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [montoAPagar, setMontoAPagar] = useState<number>(0);

    const sesionId = sesion?.id ?? null;
    const [documentoPago, setDocumentoPago] = useState<DocumentoPago>('factura');
    const documentoEditado = useRef(false);
    useEffect(() => {
        let actual = true;
        documentoEditado.current = false;
        setDocumentoPago('factura');
        if (abierto && sesionId) consultarDocumentoSesion(sesionId).then(doc => {
            if (actual && !documentoEditado.current) setDocumentoPago(doc.tipo);
        }).catch(() => {});
        return () => { actual = false; };
    }, [abierto, sesionId]);

    // Grant del operador (PIN) sobre la caja elegida: exigido por el backend
    // para cobrar; se descarta al cambiar de caja o al cerrar el modal.
    const operador = useOperadorGrant(sesionActiva?.id ?? null);
    const { reset: resetOperador } = operador;

    // ── Identidad de la operación: apertura + sesión de tratamiento + caja ──
    const mountedRef = useRef(true);
    const generationRef = useRef(0);
    const identityRef = useRef({ abierto, sesionId, cajaSeleccionadaId });
    identityRef.current = { abierto, sesionId, cajaSeleccionadaId };
    const submittingRef = useRef(false);
    const beginOperation = useCallback((cajaId: number | null) => {
        const generation = generationRef.current;
        const capturedSesion = sesionId;
        return () => mountedRef.current
            && generation === generationRef.current
            && identityRef.current.abierto
            && identityRef.current.sesionId === capturedSesion
            && identityRef.current.cajaSeleccionadaId === cajaId;
    }, [sesionId]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            generationRef.current += 1;
        };
    }, []);

    const redondear = useCallback((valor: number) =>
        Math.round((valor + Number.EPSILON) * 100) / 100, []);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .custom-scroll::-webkit-scrollbar { width: 6px; }
            .custom-scroll::-webkit-scrollbar-track { background: transparent; }
            .custom-scroll::-webkit-scrollbar-thumb { background-color: rgba(97, 37, 123, 0.3); border-radius: 20px; }
            .custom-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(97, 37, 123, 0.5); }
            .animate-fade-in-up { animation: fadeInUp 0.3s ease-out; }
            @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            .animate-pulse-slow { animation: pulse 2s infinite; }
            @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
        `;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    // ── Apertura/cierre: descubrir cajas operables; cierre invalida todo ──
    useEffect(() => {
        generationRef.current += 1;
        submittingRef.current = false;
        setIsProcessingPayment(false); setPaymentSuccess(false); setError(null);
        setCajasAbiertas([]); setCajaSeleccionadaId(null); setSesionActiva(null);
        setTiposPago([]); setTipoPagoId(''); setTipoPagoSeleccionado(null);
        setShowNoSessionPopup(false);
        resetOperador();
        if (!abierto) { setVerificandoSesion(false); setIsLoading(false); return; }
        const isCurrent = beginOperation(null);
        setVerificandoSesion(true);
        (async () => {
            try {
                const cajas = (await fetchSesionesAbiertasPorUsuario()).filter(c => c.estado === 'abierta');
                if (!isCurrent()) return;
                setCajasAbiertas(cajas);
                if (cajas.length === 0) {
                    setShowNoSessionPopup(true);
                } else if (cajas.length === 1) {
                    // Única caja operable: elección determinista (se revalida por id).
                    setCajaSeleccionadaId(cajas[0].id);
                } else {
                    // Varias cajas: la elección es del operador; nunca se elige por él.
                    setCajasAbiertas(cajas);
                }
            } catch (err) {
                if (!isCurrent()) return;
                /* Browser diagnostics omitted. */
                setShowNoSessionPopup(true);
            } finally {
                if (isCurrent()) setVerificandoSesion(false);
            }
        })();
    }, [abierto, sesionId, resetOperador, beginOperation]);

    // ── Caja elegida: revalidar por id y cargar tipos de pago ──
    useEffect(() => {
        setSesionActiva(null);
        setTiposPago([]); setTipoPagoId(''); setTipoPagoSeleccionado(null);
        setError(null);
        if (!abierto || cajaSeleccionadaId === null) return;
        const cajaId = cajaSeleccionadaId;
        const isCurrent = beginOperation(cajaId);
        setVerificandoSesion(true);
        (async () => {
            try {
                const vigente = await obtenerSesionActivaPorId(cajaId);
                if (!isCurrent()) return;
                if (!vigente || vigente.id !== cajaId) {
                    setShowNoSessionPopup(true);
                    return;
                }
                setSesionActiva(vigente);
                setIsLoading(true);
                const tipos = await fetchTiposPagoPorPos(vigente.punto_de_venta);
                if (!isCurrent()) return;
                setTiposPago(tipos);
                if (tipos.length > 0 && sesion) {
                    const primerTipo = tipos[0];
                    setTipoPagoId(primerTipo.id);
                    setTipoPagoSeleccionado(primerTipo);
                    const saldo = parseFloat(sesion.saldo_pendiente as any) || 0;
                    setMontoAPagar(saldo);
                    setEfectivoRecibido(saldo);
                }
            } catch (err: any) {
                if (!isCurrent()) return;
                /* Browser diagnostics omitted. */
                if (err?.response?.status === 409 || err?.response?.status === 404 || cajasAbiertas.length <= 1) {
                    setSesionActiva(null);
                    setShowNoSessionPopup(true);
                } else {
                    setError('No se pudo validar la caja seleccionada. Elige otra caja.');
                }
            } finally {
                if (isCurrent()) { setVerificandoSesion(false); setIsLoading(false); }
            }
        })();
        // ``cajasAbiertas`` solo decide el mensaje de error; ``sesion`` fija los montos iniciales.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abierto, cajaSeleccionadaId, beginOperation]);

    // ── Calcular cambio y sincronizar valores según configuración ──
    useEffect(() => {
        if (!sesion) return;

        const saldoPendiente = parseFloat(sesion.saldo_pendiente as any) || 0;

        if (tipoPagoSeleccionado?.configuracion_detail) {
            const config = tipoPagoSeleccionado.configuracion_detail;

            if (documentoPago === 'factura' || !config.permite_monto_personalizado) {
                setMontoAPagar(saldoPendiente);
            }

            if (config.requiere_cambio) {
                setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
            } else if (config.permite_monto_personalizado && !config.afecta_caja && efectivoRecibido > montoAPagar) {
                setCambio(redondear(efectivoRecibido - montoAPagar));
            } else {
                setCambio(0);
                if (!config.requiere_cambio && config.afecta_caja) {
                    setEfectivoRecibido(montoAPagar);
                }
            }
        } else {
            setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
        }
    }, [efectivoRecibido, montoAPagar, sesion, tipoPagoSeleccionado, redondear, documentoPago]);

    const handleTipoPagoChange = (id: number) => {
        setTipoPagoId(id);
        const tipo = tiposPago.find(t => t.id === id);
        setTipoPagoSeleccionado(tipo || null);

        if (sesion) {
            const saldoPendiente = parseFloat(sesion.saldo_pendiente as any) || 0;
            setMontoAPagar(saldoPendiente);
            setEfectivoRecibido(saldoPendiente);
        }
    };

    const handleCajaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const raw = e.target.value;
        const id = raw === '' ? null : Number(raw);
        setCajaSeleccionadaId(Number.isInteger(id) && (id as number) > 0 ? id : null);
    };

    const getIconoTipoPago = (tipo: string) => {
        switch (tipo) {
            case 'efectivo': return <Banknote className="w-4 h-4" />;
            case 'banco': return <CreditCard className="w-4 h-4" />;
            case 'otro': return <Smartphone className="w-4 h-4" />;
            default: return <Wallet className="w-4 h-4" />;
        }
    };

    const getColorConfiguracion = (afectaCaja: boolean, requiereCambio: boolean) => {
        if (!afectaCaja) return { bg: 'bg-purple-50/50', text: 'text-purple-700', border: 'border-purple-200', iconBg: 'bg-purple-100' };
        if (afectaCaja && requiereCambio) return { bg: 'bg-[#3564db]/10', text: 'text-[#3564db]', border: 'border-[#3564db]/20', iconBg: 'bg-[#3564db]/10' };
        return { bg: 'bg-[#92b1ed]/10', text: 'text-[#3564db]', border: 'border-[#92b1ed]/20', iconBg: 'bg-[#92b1ed]/10' };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Doble envío: idempotente en la UI (una sola petición en vuelo).
        if (submittingRef.current || isProcessingPayment) return;
        setError(null);
        setPaymentSuccess(false);

        if (!sesionActiva || cajaSeleccionadaId === null || sesionActiva.id !== cajaSeleccionadaId) {
            setError('Selecciona la caja POS desde la que cobras');
            return;
        }
        if (!sesion) {
            setError('No hay sesión seleccionada');
            return;
        }
        if (!operador.token) {
            setError('Identifica al operador de caja con su PIN antes de cobrar.');
            return;
        }
        const grantToken = operador.token;

        if (!tipoPagoId || !tipoPagoSeleccionado) {
            setError('Por favor seleccione un tipo de pago');
            return;
        }

        const config = tipoPagoSeleccionado.configuracion_detail;
        const saldoPendiente = parseFloat(sesion.saldo_pendiente as any) || 0;
        const montoFinal = redondear(montoAPagar);
        const efectivoRedondeado = redondear(efectivoRecibido);

        if (documentoPago === 'factura' && montoFinal !== redondear(saldoPendiente)) {
            setError('Para factura debes cubrir el saldo completo de la sesión.'); return;
        }
        if (config) {
            if (config.requiere_cambio) {
                if (efectivoRedondeado <= 0) { setError('El efectivo recibido debe ser mayor a 0'); return; }
                if (efectivoRedondeado < montoFinal) { setError('El efectivo recibido no puede ser menor al monto a pagar'); return; }
            }
            if (config.permite_monto_personalizado && montoFinal <= 0) { setError('El monto a pagar debe ser mayor a 0'); return; }
            if (config.permite_monto_personalizado && montoFinal > saldoPendiente) {
                setError(`El monto no puede superar el saldo pendiente de ${saldoPendiente.toFixed(2)} Bs.`);
                return;
            }
        }
        if (montoFinal <= 0) { setError('Debe ingresar un monto mayor a 0'); return; }

        // Identidad capturada al iniciar: sesión de tratamiento + caja explícita.
        const targetSesionId = sesion.id;
        const targetPosId = sesionActiva.id;
        const isCurrent = beginOperation(targetPosId);
        submittingRef.current = true;
        setIsProcessingPayment(true);
        try {
            const datosPago: any = {
                documento_reserva: documentoPago,
                sesion: targetSesionId,
                tipo_pago: tipoPagoId,
                monto: montoFinal,
                sesion_pos: targetPosId,
            };
            if (config?.requiere_cambio) {
                datosPago.efectivo_recibido = efectivoRedondeado;
            } else if (config?.permite_monto_personalizado && !config?.afecta_caja && efectivoRedondeado > montoFinal) {
                datosPago.efectivo_recibido = efectivoRedondeado;
            } else if (config?.afecta_caja) {
                datosPago.efectivo_recibido = montoFinal;
            }

            await createPago(datosPago, grantToken);
            if (!isCurrent()) return;
            // Éxito SOLO tras la confirmación del servidor, y solo para esta identidad.
            setPaymentSuccess(true);
            setIsProcessingPayment(false);
            submittingRef.current = false;
            onPagoExitoso();
            onCerrar();
        } catch (err: any) {
            if (!isCurrent()) return;
            setIsProcessingPayment(false);
            setPaymentSuccess(false);
            if (err?.response?.data?.detail) {
                setError(err.response.data.detail);
            } else if (err?.response?.data?.error) {
                setError(err.response.data.error);
            } else if (err?.response?.data) {
                const errores = Object.values(err.response.data).flat().join(' ');
                setError(errores);
            } else {
                setError('Error al registrar el pago');
            }
            /* Browser diagnostics omitted. */
        } finally {
            if (isCurrent()) submittingRef.current = false;
        }
    };

    const handleIrAPOS = () => {
        onCerrar();
        window.open('/pos-list', '_blank');
    };

    const handleAbrirSesionPOS = () => {
        onCerrar();
        window.open('/pos-list', '_blank');
    };

    const handleCloseNoSessionPopup = () => {
        setShowNoSessionPopup(false);
        onCerrar();
    };

    const renderNoSessionPopup = () => {
        if (!showNoSessionPopup) return null;

        return (
            <div data-luma-overlay="true" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseNoSessionPopup} />
                <CoworkBarePanel data-luma-dialog="true" className="bg-white rounded-xl w-full max-w-lg mx-auto shadow-2xl relative overflow-hidden animate-fade-in-up">
                    <div className="p-6 border-b border-[#3564db]/15 bg-gradient-to-r from-[#3564db]/10 to-[#3564db]/5">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                                <Lock className="w-6 h-6 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Sesión POS Requerida</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    Para registrar pagos, necesita una sesión activa en el Punto de Venta.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                            <div className="flex">
                                <AlertCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-amber-800 mb-1">Importante</p>
                                    <p className="text-xs text-amber-700">
                                        Sin una sesión POS activa, no es posible registrar pagos que afecten a la caja física.
                                        Esto garantiza el control de ingresos y responsabilidades.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-3 mb-6">
                            <div className="flex-1 bg-blue-50 border border-blue-100 rounded-lg p-3">
                                <div className="flex items-center mb-2">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                                        <span className="text-xs font-bold text-blue-600">1</span>
                                    </div>
                                    <h5 className="text-sm font-medium text-gray-900">Abrir Sesión</h5>
                                </div>
                                <p className="text-xs text-gray-600">Diríjase al módulo POS y abra una nueva sesión.</p>
                            </div>
                            <div className="flex-1 bg-blue-50 border border-blue-100 rounded-lg p-3">
                                <div className="flex items-center mb-2">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                                        <span className="text-xs font-bold text-blue-600">2</span>
                                    </div>
                                    <h5 className="text-sm font-medium text-gray-900">Configurar</h5>
                                </div>
                                <p className="text-xs text-gray-600">Establezca el monto inicial de caja y confirme los datos.</p>
                            </div>
                            <div className="flex-1 bg-blue-50 border border-blue-100 rounded-lg p-3">
                                <div className="flex items-center mb-2">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                                        <span className="text-xs font-bold text-blue-600">3</span>
                                    </div>
                                    <h5 className="text-sm font-medium text-gray-900">Registrar</h5>
                                </div>
                                <p className="text-xs text-gray-600">Vuelva y proceda con el registro del pago.</p>
                            </div>
                        </div>
                    </div>

                    <div className="ps-footer">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button onClick={handleCloseNoSessionPopup} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors flex-1">
                                Cancelar
                            </button>
                            <button onClick={handleAbrirSesionPOS} className="px-4 py-2.5 text-sm font-medium bg-[#3564db] hover:bg-[#0069a6] text-white rounded-lg transition-colors flex-1 flex items-center justify-center gap-2">
                                <Banknote className="w-4 h-4" />
                                Abrir Sesión POS
                                <ExternalLink className="w-4 h-4 ml-1 opacity-70" />
                            </button>
                        </div>
                        <div className="mt-3">
                            <button onClick={handleIrAPOS} className="w-full px-4 py-2.5 text-sm text-[#3564db] hover:text-[#0069a6] bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center justify-center gap-2">
                                <CreditCard className="w-4 h-4" />
                                Ver estado actual del POS
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-3">Al abrir una sesión POS, acepta las políticas de control de caja.</p>
                    </div>
                </CoworkBarePanel>
            </div>
        );
    };

    if (!abierto) return null;

    if (showNoSessionPopup) {
        return renderNoSessionPopup();
    }

    if (verificandoSesion && cajasAbiertas.length <= 1) {
        return (
            <div data-luma-overlay="true" className="fixed inset-0 z-[100] flex items-center justify-center">
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onCerrar} />
                <CoworkBarePanel data-luma-dialog="true" className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl relative p-8">
                    <Loading compact text="Verificando sesión POS activa…"/>
                </CoworkBarePanel>
            </div>
        );
    }

    if (!sesion) {
        return (
            <div data-luma-overlay="true" className="fixed inset-0 z-[100] flex items-center justify-center">
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={isProcessingPayment ? undefined : onCerrar} />
                <CoworkBarePanel data-luma-dialog="true" className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl relative p-8">
                    <div className="text-center">
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error</h3>
                        <p className="text-gray-600">No hay sesión de reserva seleccionada</p>
                        <button onClick={onCerrar} className="mt-6 px-4 py-2 bg-[#3564db] text-white rounded-lg disabled:opacity-50" disabled={isProcessingPayment}>
                            Cerrar
                        </button>
                    </div>
                </CoworkBarePanel>
            </div>
        );
    }

    const pagado = parseFloat(sesion.monto_pagado as any) || 0;
    const saldoPendiente = parseFloat(sesion.saldo_pendiente as any) || 0;
    const precioConDescuento = parseFloat(sesion.precio_con_descuento as any) || 0;

    const config = tipoPagoSeleccionado?.configuracion_detail;
    const permitePersonalizado = documentoPago === 'prefactura' && (config?.permite_monto_personalizado ?? false);
    const esPagoDigitalConCambio = permitePersonalizado && !config?.afecta_caja && efectivoRecibido > montoAPagar;
    const cajaOperable = !!sesionActiva && cajaSeleccionadaId !== null && sesionActiva.id === cajaSeleccionadaId;
    const puedeCobrar = cajaOperable && !!operador.token && !isLoading && !verificandoSesion && saldoPendiente > 0 && !isProcessingPayment;

    return (
        <div data-luma-overlay="true" className="ps-overlay fixed inset-0 z-[100] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={isProcessingPayment ? undefined : onCerrar} />
            <div className="ps-shell relative flex flex-col overflow-hidden">
                {/* Header */}
                <div className="ps-header">
                    <div className="flex justify-between items-center">
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {isProcessingPayment ? 'Registrando Pago...' : `Pago de sesión #${sesion.id}`}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {isProcessingPayment ? 'Por favor espere...' : 'Selecciona el POS, identifica al operador y registra el pago de este período'}
                            </p>

                        </div>
                        <button
                            onClick={isProcessingPayment ? undefined : onCerrar}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isProcessingPayment}
                            aria-label="Cerrar"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="ps-form flex flex-col">
                    <div className="ps-scroll custom-scroll">
                        <DocumentoPagoSelector value={documentoPago}
                            onChange={tipo => { documentoEditado.current = true; setDocumentoPago(tipo); }}
                            disabled={isProcessingPayment} destino="esta sesión" />
                        <div>
                            {cajasAbiertas.length > 0 && (
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="pago-sesion-caja">
                                        Caja POS *
                                    </label>
                                    <select
                                        id="pago-sesion-caja"
                                        aria-label="Caja POS"
                                        value={cajaSeleccionadaId ?? ''}
                                        onChange={handleCajaChange}
                                        disabled={isProcessingPayment}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <option value="">Selecciona la caja desde la que cobras</option>
                                        {cajasAbiertas.map(caja => (
                                            <option key={caja.id} value={caja.id}>
                                                {caja.punto_de_venta_nombre} · {caja.codigo || `#${caja.id}`} · {caja.sucursal_nombre}
                                            </option>
                                        ))}
                                    </select>
                                    {cajaSeleccionadaId === null && (
                                        <p role="note" className="text-xs text-amber-700 mt-1">
                                            Tienes varias cajas abiertas: elige explícitamente desde cuál registras el pago.
                                        </p>
                                    )}
                                </div>
                            )}
                            {cajaOperable && sesionActiva && (
                                <div className="mt-2 flex items-center gap-2">
                                    <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#3564db]/10 text-[#3564db]">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Sesión POS Activa
                                    </div>
                                    <span className="text-xs text-gray-500">
                                        {sesionActiva.punto_de_venta_nombre} • {sesionActiva.codigo}
                                    </span>
                                </div>
                            )}
                            {cajaOperable && (
                                <div className="mt-3">
                                    <OperadorGrantRow grant={operador.grant} disabled={isProcessingPayment} onIdentify={operador.openDialog} />
                                </div>
                            )}</div>

                        {/* Estado de procesamiento: el éxito solo aparece tras la respuesta del servidor */}
                        {isProcessingPayment && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 animate-pulse-slow">
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600 mr-3"></div>
                                    <div>
                                        <p className="font-medium text-blue-800">Procesando pago...</p>
                                        <p className="text-sm text-blue-600 mt-1">Por favor no cierre esta ventana ni presione el botón nuevamente</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {paymentSuccess && !isProcessingPayment && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <p className="font-medium text-green-800">Pago registrado exitosamente</p>
                            </div>
                        )}

                        {/* Error */}
                        {error && !isProcessingPayment && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" role="alert">
                                <p className="text-sm font-medium">{error}</p>
                            </div>
                        )}

                        {/* Resumen de Montos */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="flex items-center mb-2">
                                    <div className="w-8 h-8 rounded-lg bg-[#3564db]/10 flex items-center justify-center mr-2">
                                        <Banknote className="w-4 h-4 text-[#3564db]" />
                                    </div>
                                    <p className="text-xs text-gray-500">Total Sesión</p>
                                </div>
                                <p className="font-semibold text-gray-900">{precioConDescuento.toFixed(2)} <span className="text-sm text-gray-500">Bs.</span></p>
                            </div>

                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="flex items-center mb-2">
                                    <div className="w-8 h-8 rounded-lg bg-[#92b1ed]/10 flex items-center justify-center mr-2">
                                        <CheckCircle className="w-4 h-4 text-[#3564db]" />
                                    </div>
                                    <p className="text-xs text-gray-500">Pagado</p>
                                </div>
                                <p className="font-semibold text-gray-900">{pagado.toFixed(2)} <span className="text-sm text-gray-500">Bs.</span></p>
                            </div>

                            <div className={`rounded-lg border p-4 ${saldoPendiente > 0 ? 'bg-[#ffd97c]/10 border-[#ffd97c]/30' : 'bg-gray-50 border-gray-200'}`}>
                                <div className="flex items-center mb-2">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-2 ${saldoPendiente > 0 ? 'bg-[#ffd97c]/20' : 'bg-gray-100'}`}>
                                        <Wallet className={`w-4 h-4 ${saldoPendiente > 0 ? 'text-[#cc6c00]' : 'text-gray-600'}`} />
                                    </div>
                                    <p className="text-xs text-gray-500">Saldo Pendiente</p>
                                </div>
                                <p className="font-semibold text-gray-900">{Math.max(0, saldoPendiente).toFixed(2)} <span className="text-sm text-gray-500">Bs.</span></p>
                            </div>
                        </div>

                        {/* Tipo de Pago */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Pago *</label>
                            <div className="ps-methods" role="group" aria-label="Tipo de Pago">
                                {tiposPago.map(tipo => <button key={tipo.id} type="button"
                                    aria-pressed={tipoPagoId === tipo.id}
                                    onClick={() => handleTipoPagoChange(tipo.id)}
                                    disabled={isLoading || !cajaOperable || isProcessingPayment}>
                                    {getIconoTipoPago(tipo.configuracion_detail?.tipo || '')}<span>{tipo.nombre}</span>
                                </button>)}
                            </div>

                            {config && (
                                <div className={`mt-3 p-4 border rounded-lg ${getColorConfiguracion(config.afecta_caja, config.requiere_cambio).bg} ${getColorConfiguracion(config.afecta_caja, config.requiere_cambio).border}`}>
                                    <div className="flex items-start">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mr-3 ${getColorConfiguracion(config.afecta_caja, config.requiere_cambio).iconBg}`}>
                                            <div className={getColorConfiguracion(config.afecta_caja, config.requiere_cambio).text}>
                                                {getIconoTipoPago(config.tipo)}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <p className={`font-medium ${getColorConfiguracion(config.afecta_caja, config.requiere_cambio).text}`}>
                                                {config.nombre}
                                            </p>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.afecta_caja ? 'bg-[#92b1ed]/20 text-[#3564db]' : 'bg-purple-100 text-purple-700'}`}>
                                                    {config.afecta_caja ? 'Afecta a caja' : 'No afecta a caja'}
                                                </span>
                                                {config.requiere_cambio && (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#3564db]/20 text-[#3564db]">
                                                        Requiere cambio
                                                    </span>
                                                )}
                                                {config.permite_monto_personalizado && (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                        <SplitSquareHorizontal className="w-3 h-3 mr-1" />
                                                        Pago parcial permitido
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Monto a Pagar */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Monto a Pagar *
                                {permitePersonalizado && (
                                    <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                                        Monto libre
                                    </span>
                                )}
                            </label>
                            <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                max={saldoPendiente}
                                value={montoAPagar}
                                onChange={(e) => {
                                    if (permitePersonalizado) {
                                        const val = parseFloat(e.target.value);
                                        const nuevoMonto = isNaN(val) ? 0 : redondear(Math.min(val, saldoPendiente));
                                        setMontoAPagar(nuevoMonto);
                                        if (!config?.requiere_cambio) {
                                            setEfectivoRecibido(nuevoMonto);
                                        }
                                    }
                                }}
                                readOnly={!permitePersonalizado || isProcessingPayment}
                                disabled={isProcessingPayment}
                                className={`w-full px-3 py-2.5 border rounded-lg font-medium text-gray-900 focus:outline-none transition-colors
                                    ${permitePersonalizado
                                        ? 'border-purple-300 focus:ring-2 focus:ring-purple-200 focus:border-purple-400 bg-white'
                                        : 'border-gray-300 bg-gray-50'
                                    }`}
                            />
                            {permitePersonalizado && (
                                <p className="text-xs text-purple-600 mt-1">
                                    Puede ingresar un monto parcial (máx: {saldoPendiente.toFixed(2)} Bs.)
                                </p>
                            )}
                        </div>

                        {/* Efectivo Recibido */}
                        {(config?.afecta_caja || esPagoDigitalConCambio || (permitePersonalizado && !config?.afecta_caja)) && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {config?.requiere_cambio || esPagoDigitalConCambio
                                        ? 'Monto Recibido *'
                                        : 'Monto a Pagar'
                                    }
                                    {!config?.afecta_caja && permitePersonalizado && (
                                        <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                                            QR / Digital
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={efectivoRecibido}
                                    onChange={(e) => {
                                        const valor = parseFloat(e.target.value);
                                        setEfectivoRecibido(isNaN(valor) ? 0 : redondear(valor));
                                    }}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={
                                        isLoading ||
                                        isProcessingPayment ||
                                        (!config?.requiere_cambio && !(permitePersonalizado && !config?.afecta_caja))
                                    }
                                    readOnly={
                                        (!config?.requiere_cambio && !(permitePersonalizado && !config?.afecta_caja)) ||
                                        isProcessingPayment
                                    }
                                />
                                <p className="text-xs text-gray-500 mt-2">
                                    {config?.requiere_cambio
                                        ? 'Ingrese el monto recibido para calcular el cambio'
                                        : permitePersonalizado && !config?.afecta_caja
                                            ? 'Si recibe más del monto, el cambio se registrará como salida de caja en efectivo'
                                            : 'Pago exacto - no requiere cambio'
                                    }
                                </p>
                            </div>
                        )}

                        {/* Cambio a Entregar */}
                        {(config?.requiere_cambio || esPagoDigitalConCambio) && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Cambio a Entregar
                                    {esPagoDigitalConCambio && !config?.afecta_caja && (
                                        <span className="ml-2 text-xs font-normal text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                                            Salida de caja en efectivo
                                        </span>
                                    )}
                                </label>
                                <div className={`w-full px-3 py-2.5 border rounded-lg font-semibold ${cambio > 0
                                    ? 'border-[#92b1ed]/30 bg-[#92b1ed]/10 text-[#3564db]'
                                    : 'border-gray-300 bg-gray-50 text-gray-900'
                                    }`}>
                                    {cambio.toFixed(2)} <span className="text-sm font-normal">Bs.</span>
                                </div>
                                {esPagoDigitalConCambio && cambio > 0 && (
                                    <p className="text-xs text-orange-600 mt-1">
                                        Este cambio se registrará como salida de caja física de {cambio.toFixed(2)} Bs.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Advertencia pago digital sin cambio */}
                        {!config?.afecta_caja && tipoPagoSeleccionado && !isProcessingPayment && !esPagoDigitalConCambio && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center mr-3">
                                        <Smartphone className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-purple-800 text-sm">Pago Digital</p>
                                        <p className="text-sm text-purple-600 mt-1">
                                            {permitePersonalizado
                                                ? 'Si el cliente envía más del monto indicado, ingrese el monto recibido para calcular el cambio a devolver.'
                                                : 'Este pago no afectará a la caja física. El movimiento de caja no se registrará.'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="ps-footer">
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onCerrar}
                                className="px-4 py-2.5 text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isProcessingPayment}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2.5 text-sm bg-[#3564db] hover:bg-[#92b1ed] text-white rounded-lg transition-colors font-medium flex items-center justify-center min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm relative"
                                disabled={!puedeCobrar}
                                title={cajaOperable && !operador.token ? 'Identifica al operador de caja con su PIN para cobrar' : undefined}
                            >
                                {isProcessingPayment ? (
                                    <>
                                        <div className="absolute inset-0 bg-[#3564db] rounded-lg animate-pulse"></div>
                                        <div className="relative flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Registrando...
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="w-4 h-4 mr-2" />
                                        Registrar Pago
                                    </>
                                )}
                            </button>
                        </div>
                        {isProcessingPayment && (
                            <p className="text-xs text-center text-gray-500 mt-3">Por favor espere, esto puede tomar unos segundos...</p>
                        )}
                    </div>
                </form>
            </div>
            <OperadorPinDialog open={operador.dialogOpen} sesionId={cajaOperable ? sesionActiva?.id ?? null : null} onClose={operador.closeDialog} onGrant={operador.install} />
        </div>
    );
};

export default PagoSesionModal;




